import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    styled,
    useTheme
} from '@mui/material';
import { Close, LocationOn, CalendarMonth, Group } from '@mui/icons-material';
import { formatDate } from './formatDate';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: "16px 24px",
    color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
    whiteSpace: "nowrap",
    transition: "background-color 0.2s ease",
    fontSize: "0.875rem",
    
    "&.header": {
        fontWeight: 600,
        fontSize: "0.875rem",
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
        letterSpacing: "0.02em",
        color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.dark,
    },
    "&:first-of-type": {
        paddingLeft: 32,
    },
    "&:last-of-type": {
        paddingRight: 32,
    }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    transition: "all 0.2s ease",
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.01)' : 'rgba(255, 255, 255, 0.5)',
    
    "&:hover": {
        backgroundColor: theme.palette.mode === 'dark' 
            ? "rgba(255, 255, 255, 0.05)" 
            : "rgba(0, 0, 0, 0.02)",
        transform: "translateY(-1px)",
        boxShadow: theme.palette.mode === 'dark'
            ? '0 2px 8px rgba(0, 0, 0, 0.3)'
            : '0 2px 8px rgba(0, 0, 0, 0.05)',
    },
    
    "& > .MuiTableCell-root": {
        borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
    },
}));



const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : theme.palette.common.white,
        maxHeight: '90vh',
    }
}));

const ScrollableContent = styled(DialogContent)(({ theme }) => ({
    padding: theme.spacing(3),
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
    },
    '&::-webkit-scrollbar-track': {
        background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
        borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
        borderRadius: '4px',
        '&:hover': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
        },
    },
}));


const TableContainer = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.01)' : theme.palette.common.white,
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: theme.palette.mode === 'dark'
        ? '0 4px 20px rgba(0, 0, 0, 0.4)'
        : '0 4px 20px rgba(0, 0, 0, 0.05)',
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
}));

const EventAttendeeModal = ({ open, onClose, eventData, attendees }) => {
    const theme = useTheme();

    return (
        <StyledDialog
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle 
                sx={{ 
                    m: 0, 
                    p: 3,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Group sx={{ color: theme.palette.primary.main }} />
                    <Typography variant="h6" component="div">
                        Event Attendees
                    </Typography>
                </Box>
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 16,
                        top: 16,
                        color: theme.palette.mode === 'dark' ? theme.palette.grey[400] : theme.palette.grey[500],
                    }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>

            <ScrollableContent>
                {eventData && (
                    <Box 
                        sx={{ 
                            mb: 3,
                            p: 2.5,
                            borderRadius: 1,
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                        }}
                    >
                        <Typography 
                            variant="h6" 
                            sx={{ 
                                mb: 2,
                                color: theme.palette.primary.main,
                                fontWeight: 500
                            }}
                        >
                            {eventData.title}
                        </Typography>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            mb: 1,
                            color: theme.palette.mode === 'dark' ? theme.palette.grey[400] : theme.palette.grey[700],
                        }}>
                            <CalendarMonth fontSize="small" />
                            <Typography variant="body2">
                                {formatDate(eventData.start_day, eventData.end_day)}
                            </Typography>
                        </Box>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            color: theme.palette.mode === 'dark' ? theme.palette.grey[400] : theme.palette.grey[700],
                        }}>
                            <LocationOn fontSize="small" />
                            <Typography variant="body2">
                                {eventData.city}, {eventData.address}
                            </Typography>
                        </Box>
                        {eventData.description && (
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    mt: 2,
                                    color: theme.palette.mode === 'dark' ? theme.palette.grey[400] : theme.palette.grey[600],
                                }}
                            >
                                {eventData.description}
                            </Typography>
                        )}
                    </Box>
                )}

                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    mb: 2,
                    position: 'sticky',
                    top: 0,
                    backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : theme.palette.common.white,
                    zIndex: 1,
                    py: 1,
                }}>
                    <Group fontSize="small" sx={{ color: theme.palette.primary.main }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        Attendees List ({attendees?.length || 0})
                    </Typography>
                </Box>
                
                <TableContainer> 
                   
            
                    
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell 
                                    className="header"
                                   
                                >
                                    Name
                                </StyledTableCell>
                                <StyledTableCell 
                                    className="header"
                                    // sx={{
                                    //     backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#fff',
                                    // }}
                                >
                                    Email
                                </StyledTableCell>
                                <StyledTableCell 
                                    className="header"
                                    // sx={{
                                    //     backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#fff',
                                    // }}
                                >
                                    Contact
                                </StyledTableCell>
                                <StyledTableCell 
                                    className="header"
                                    // sx={{
                                    //     backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#fff',
                                    // }}
                                >
                                    Message
                                </StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {attendees?.length > 0 ? (
                                attendees.map((attendee) => (
                                    <StyledTableRow key={attendee.id}>
                                        <StyledTableCell>{attendee.full_name}</StyledTableCell>
                                        <StyledTableCell>{attendee.email}</StyledTableCell>
                                        <StyledTableCell>{attendee.contact_no}</StyledTableCell>
                                        <StyledTableCell>{attendee.message}</StyledTableCell>
                                    </StyledTableRow>
                                ))
                            ) : (
                                <StyledTableRow>
                                    <StyledTableCell 
                                        colSpan={4} 
                                        align="center"
                                        sx={{ 
                                            py: 3,
                                            color: theme.palette.mode === 'dark' ? theme.palette.grey[400] : theme.palette.grey[600]
                                        }}
                                    >
                                        No attendees found
                                    </StyledTableCell>
                                </StyledTableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </ScrollableContent>
        </StyledDialog>
    );
};

export default EventAttendeeModal;